<template>
  <teleport to="body">
    <transition name="dialog">
      <div v-if="modelValue" class="dialog-overlay" :style="overlayStyle" @mousedown.self="onOverlayMouseDown" @mouseup.self="onOverlayMouseUp">
        <div :class="dialogClasses" :style="dialogStyle">
          <div v-if="title || $slots.header" class="dialog__header">
            <slot name="header">
              <h2 class="dialog__title">{{ title }}</h2>
            </slot>
            <button v-if="closable" type="button" class="dialog__close" @click="close">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
          <div class="dialog__body" :style="bodyStyle">
            <slot />
          </div>
          <div v-if="$slots.footer" class="dialog__footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
export default {
  name: 'BaseDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    width: {
      type: [String, Number],
      default: 500
    },
    maxWidth: {
      type: [String, Number],
      default: '90vw'
    },
    closable: {
      type: Boolean,
      default: true
    },
    closeOnOverlay: {
      type: Boolean,
      default: true
    },
    persistent: {
      type: Boolean,
      default: false
    },
    fullscreen: {
      type: Boolean,
      default: false
    },
    bodyStyle: {
      type: Object,
      default: () => ({})
    },
    overlayStyle: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:modelValue', 'close'],
  computed: {
    dialogClasses() {
      return ['dialog', this.fullscreen ? 'dialog--fullscreen' : '']
    },
    dialogStyle() {
      if (this.fullscreen) return {}
      const width = typeof this.width === 'number' ? `${this.width}px` : this.width
      const maxWidth = typeof this.maxWidth === 'number' ? `${this.maxWidth}px` : this.maxWidth
      return { width, maxWidth }
    }
  },
  watch: {
    modelValue(val) {
      if (val) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    }
  },
  beforeUnmount() {
    document.body.style.overflow = ''
  },
  data() {
    return {
      overlayMouseDownFired: false
    }
  },
  methods: {
    onOverlayMouseDown() {
      this.overlayMouseDownFired = true
    },
    onOverlayMouseUp() {
      if (this.overlayMouseDownFired && this.closeOnOverlay) {
        this.close()
      }
      this.overlayMouseDownFired = false
    },
    close() {
      if (!this.persistent) {
        this.$emit('update:modelValue', false)
        this.$emit('close')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../scss/variables' as *;

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: $dialog-backdrop-color;
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $spacing-4;
  z-index: $z-index-modal;
}

.dialog {
  background-color: $color-bg-primary;
  border-radius: $dialog-border-radius;
  box-shadow: $dialog-shadow;
  max-height: 90vh;
  max-height: 90dvh;   // 휴대폰 주소창 뒤로 아래 버튼이 가려지지 않게(보이는 높이 기준)
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $spacing-5 $spacing-6;
    background: linear-gradient(135deg, $color-primary 0%, $color-primary-dark 100%);
    color: $color-text-inverse;
  }

  &__title {
    font-size: $font-size-lg;
    font-weight: $font-weight-semibold;
    color: inherit;
    margin: 0;
    letter-spacing: $letter-spacing-tight;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    background: rgba(255, 255, 255, 0.15);
    border-radius: $radius-lg;
    color: inherit;
    cursor: pointer;
    transition: all $transition-fast $transition-timing;

    svg {
      width: 20px;
      height: 20px;
    }

    &:hover {
      background-color: rgba(255, 255, 255, 0.25);
    }
  }

  &__body {
    flex: 1;
    padding: 0;
    overflow-y: auto;
    background-color: $color-bg-primary;

    // Custom scrollbar for dialog body
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: $color-gray-300;
      border-radius: 3px;
    }
  }

  &--fullscreen {
    width: 100vw !important;
    max-width: 100vw !important;
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border-radius: 0;

    .dialog__body {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: $spacing-3;
    padding: $spacing-4 $spacing-6;
    background-color: $color-bg-secondary;
    border-top: 1px solid $color-border-light;
  }
}

// Animation
.dialog-enter-active,
.dialog-leave-active {
  transition: opacity $transition-normal $transition-timing;

  .dialog {
    transition: transform $transition-normal $transition-timing,
                opacity $transition-normal $transition-timing;
  }
}

.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;

  .dialog {
    transform: scale(0.95) translateY(-10px);
    opacity: 0;
  }
}
</style>
